import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { Zap, DollarSign, Shield, Bot, MessageSquare, Settings, Eye, Code, Smartphone, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../supabaseClient';
import './Home.css';

const TYPEWRITER_WORDS = ['Intelligence', 'Automation', 'Innovation', 'Excellence'];

function useTypewriter(words) {
  const [display, setDisplay] = useState('');
  const [wordIdx, setWordIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[wordIdx];
    const timeout = setTimeout(() => {
      if (!deleting) {
        setDisplay(current.slice(0, charIdx + 1));
        if (charIdx + 1 === current.length) {
          setTimeout(() => setDeleting(true), 1800);
        } else {
          setCharIdx(c => c + 1);
        }
      } else {
        setDisplay(current.slice(0, charIdx - 1));
        if (charIdx - 1 === 0) {
          setDeleting(false);
          setWordIdx(i => (i + 1) % words.length);
          setCharIdx(0);
        } else {
          setCharIdx(c => c - 1);
        }
      }
    }, deleting ? 50 : 90);
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, wordIdx, words]);

  return display;
}

function CounterCard({ value, label, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const num = parseInt(value);
        const duration = 1800;
        const steps = 50;
        const increment = num / steps;
        let current = 0;
        const timer = setInterval(() => {
          current += increment;
          if (current >= num) { setCount(num); clearInterval(timer); }
          else setCount(Math.floor(current));
        }, duration / steps);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div className="counter-card glass-card" ref={ref}>
      <div className="counter-value">{count}{suffix}</div>
      <div className="counter-label">{label}</div>
    </div>
  );
}

function Home() {
  usePageTitle('AI Engineering & Vision Solutions');
  const clientsScrollRef = useRef(null);
  const [clients, setClients]             = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const typed = useTypewriter(TYPEWRITER_WORDS);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('visible');
      });
    }, { threshold: 0.08 });
    document.querySelectorAll('.fade-in').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    supabase
      .from('clients')
      .select('*')
      .then(({ data }) => { if (data) setClients(data); })
      .catch(() => {})
      .finally(() => setClientsLoading(false));
  }, []);

  const scrollClients = (dir) => {
    if (clientsScrollRef.current) {
      clientsScrollRef.current.scrollBy({ left: dir === 'left' ? -400 : 400, behavior: 'smooth' });
    }
  };

  return (
    <div className="home">
      {/* HERO */}
      <section className="home-hero">
        <div className="hero-animated-bg">
          <div className="animated-shape shape-1"></div>
          <div className="animated-shape shape-2"></div>
          <div className="animated-shape shape-3"></div>
          <div className="animated-shape shape-4"></div>
        </div>
        <div className="hero-grid-overlay"></div>
        <div className="home-container">
          <div className="hero-content fade-in">
            <div className="hero-badge">
              <span className="glow-dot"></span>
              AI-First Product Engineering
            </div>
            <h1 className="hero-title">
              Engineering <span className="hero-typed">{typed}<span className="cursor">|</span></span>
              <br />into Business
            </h1>
            <p className="hero-subtitle">
              We build production-grade AI platforms and intelligent automation systems that drive real business outcomes. Not demos. Real systems.
            </p>
            <div className="hero-cta">
              <Link to="/contact" className="btn-primary">Book Consultation <ArrowRight size={16} /></Link>
              <Link to="/case-studies" className="btn-secondary">See Our Work</Link>
            </div>
          </div>
        </div>
        <div className="hero-scroll-indicator">
          <div className="scroll-line"></div>
        </div>
      </section>

      {/* STATS */}
      <section className="home-stats fade-in">
        <div className="home-container">
          <div className="stats-grid">
            <CounterCard value="50" suffix="+" label="Projects Delivered" />
            <CounterCard value="40" suffix="%" label="Faster Time to Market" />
            <CounterCard value="60" suffix="%" label="Cost Advantage" />
            <CounterCard value="90" suffix=" days" label="Average ROI Timeline" />
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="home-trust fade-in">
        <div className="home-container">
          <h2 className="section-title">Why Companies Trust Biltzentech</h2>
          <p className="section-subtitle">Enterprise-grade engineering with startup agility</p>
          <div className="trust-grid">
            <div className="trust-card glass-card">
              <div className="trust-icon"><Zap size={22} /></div>
              <h3>Faster Delivery</h3>
              <p>Agile sprints and proven frameworks get your product to market 40% faster than traditional agencies.</p>
            </div>
            <div className="trust-card glass-card">
              <div className="trust-icon"><DollarSign size={22} /></div>
              <h3>Cost Advantage</h3>
              <p>Enterprise-grade engineering at 60% lower cost than Western agencies without compromising quality.</p>
            </div>
            <div className="trust-card glass-card">
              <div className="trust-icon"><Shield size={22} /></div>
              <h3>Production Quality</h3>
              <p>Scalable architecture, security-first design, and DevOps excellence built into every project.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <section className="home-clients fade-in">
        <div className="home-container">
          <h2 className="section-title">Trusted by Companies Building the Future</h2>
          {clientsLoading ? (
            <div className="clients-skeleton" aria-label="Loading clients…" aria-busy="true">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="client-skeleton-item" />
              ))}
            </div>
          ) : clients.length > 0 ? (
            <div className="clients-wrapper">
              <button className="scroll-btn" onClick={() => scrollClients('left')} aria-label="Scroll clients left">
                <ChevronLeft size={20} aria-hidden="true" />
              </button>
              <div className="clients-scroll" ref={clientsScrollRef}>
                {clients.map(client => (
                  <div key={client.id} className="client-item glass-card">
                    <img src={client.logo} alt={`${client.name} logo`} loading="lazy" />
                    <p>{client.name}</p>
                  </div>
                ))}
              </div>
              <button className="scroll-btn" onClick={() => scrollClients('right')} aria-label="Scroll clients right">
                <ChevronRight size={20} aria-hidden="true" />
              </button>
            </div>
          ) : (
            <p className="clients-placeholder">
              We partner with startups, SMBs, and enterprises across manufacturing, healthcare, fintech, and more.{' '}
              <Link to="/case-studies" style={{ color: '#818cf8', textDecoration: 'underline' }}>See our work →</Link>
            </p>
          )}
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="home-capabilities fade-in">
        <div className="home-container">
          <h2 className="section-title">Our Core Capabilities</h2>
          <p className="section-subtitle">Full-spectrum AI and software engineering services</p>
          <div className="capabilities-grid">
            {[
              { icon: <Bot size={24} />, title: 'Generative AI Applications', desc: 'Custom GPT solutions, RAG systems, and LLM integrations built for production.' },
              { icon: <MessageSquare size={24} />, title: 'Conversational AI Avatars', desc: 'Human-like AI agents with voice and video capabilities for real engagement.' },
              { icon: <Settings size={24} />, title: 'AI Automation', desc: 'Workflow automation and intelligent process optimization at scale.' },
              { icon: <Eye size={24} />, title: 'Computer Vision Systems', desc: 'Defect detection, surveillance, and visual intelligence for industry.' },
              { icon: <Code size={24} />, title: 'Custom Software Development', desc: 'Full-stack web and mobile application development with modern stacks.' },
              { icon: <Smartphone size={24} />, title: 'Mobile & Web Platforms', desc: 'React, React Native, Node.js, Python ecosystems — end to end.' },
            ].map((cap, i) => (
              <div key={i} className="capability-card glass-card">
                <div className="capability-icon">{cap.icon}</div>
                <h3>{cap.title}</h3>
                <p>{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATOR */}
      <section className="home-differentiator fade-in">
        <div className="home-container">
          <div className="diff-content">
            <h2 className="section-title">Beyond Chatbots: Real AI That Works</h2>
            <p className="diff-text">We don't build AI demos. We engineer agentic workflows that automate complex business processes, reduce operational costs by 50–70%, and deliver measurable ROI within 90 days.</p>
            <div className="diff-list">
              {[
                'Agentic AI systems that take actions, not just answer questions',
                'Multi-step automation workflows across your entire business',
                'Integration with existing tools and databases',
                'Real-time analytics and performance monitoring',
              ].map((item, i) => (
                <div key={i} className="diff-item">
                  <span className="diff-check"><ArrowRight size={14} /></span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="home-usecases fade-in">
        <div className="home-container">
          <h2 className="section-title">Featured Use Cases</h2>
          <p className="section-subtitle">Real results from real projects</p>
          <div className="usecases-grid">
            {[
              { title: 'Manufacturing Defect Detection', desc: 'AI-powered visual inspection reducing defects by 95%', metric: '95%' },
              { title: 'AI Customer Support Agent', desc: '24/7 intelligent support handling 80% of queries autonomously', metric: '80%' },
              { title: 'Sales Automation Assistant', desc: 'Lead qualification and outreach automation increasing conversions 3x', metric: '3x' },
              { title: 'Healthcare Document Intelligence', desc: 'Medical record processing and analysis with 99% accuracy', metric: '99%' },
              { title: 'Fitness Membership Engagement', desc: 'AI-driven personalization boosting retention by 45%', metric: '45%' },
            ].map((uc, i) => (
              <div key={i} className="usecase-card glass-card">
                <div className="usecase-metric">{uc.metric}</div>
                <h3>{uc.title}</h3>
                <p>{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT */}
      <section className="home-engagement fade-in">
        <div className="home-container">
          <h2 className="section-title">Two Ways to Work With Us</h2>
          <p className="section-subtitle">Choose the model that fits your needs</p>
          <div className="engagement-grid">
            <div className="engagement-card glass-card">
              <div className="engagement-number">01</div>
              <h3>We Build Your Product</h3>
              <p>End-to-end product development from concept to deployment</p>
              <ul>
                {['Fixed Scope Projects', 'MVP Development Sprints', 'AI Consulting & Strategy', 'Ongoing Support & Scaling'].map((item, i) => (
                  <li key={i}><ArrowRight size={14} />{item}</li>
                ))}
              </ul>
              <Link to="/services" className="engagement-link">Explore Services <ArrowRight size={14} /></Link>
            </div>
            <div className="engagement-card glass-card">
              <div className="engagement-number">02</div>
              <h3>We Provide Engineers</h3>
              <p>Dedicated remote teams that integrate with your workflow</p>
              <ul>
                {['Staff Augmentation', 'Dedicated Development Teams', 'AI/ML Engineers', 'Full-Stack Developers'].map((item, i) => (
                  <li key={i}><ArrowRight size={14} />{item}</li>
                ))}
              </ul>
              <Link to="/staffing" className="engagement-link">View Staffing Options <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="home-cta fade-in">
        <div className="home-container">
          <div className="cta-box">
            <div className="cta-glow"></div>
            <h2>Ready to Build Something Intelligent?</h2>
            <p>Schedule a free 30-minute consultation to discuss your AI and software needs</p>
            <Link to="/contact" className="btn-primary">Book Your Consultation <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
